import { Card, Divider, Group, Text, rem } from '@mantine/core';
import { Fragment } from 'react';
import type { OwnerStatement } from '../../statement/_types';
import { PlaceholderBadge } from './PlaceholderBagde';

type LedgerStatementRevenueSummaryLine = Pick<
  OwnerStatement['rows'][0]['columns'][number],
  'name' | 'text'
> & {
  separated?: boolean;
  props?: {
    separated?: boolean;
  };
};

type Props = {
  mode: 'view' | 'template';
  title: string;
  lines: LedgerStatementRevenueSummaryLine[];
  headerLabel?: string;
  lineStyles?: Partial<
    Record<string, { isBold?: boolean; isPrimary?: boolean }>
  >;
};

export const LedgerStatementRevenueSummary = ({
  mode,
  title,
  lines,
  headerLabel,
  lineStyles,
}: Props) => {
  const resolvedHeaderLabel = headerLabel || 'Statement Summary';

  return (
    <Card
      p="md"
      radius="md"
      withBorder
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: rem(10),
        backgroundColor: theme.colors.neutral[0],
        borderColor: theme.colors.neutral[1],
      })}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: rem(8) }}>
        <Text
          component="p"
          m={0}
          c="neutral.5"
          fz={rem(12)}
          lh={1.4}
          style={{
            letterSpacing: '0.1px',
          }}
        >
          {resolvedHeaderLabel}
        </Text>
        <Text
          component="p"
          m={0}
          c="neutral.8"
          fw={700}
          fz={rem(16)}
          lh={1.4}
          style={{
            letterSpacing: '-0.18px',
          }}
        >
          {mode === 'view' ? title : <PlaceholderBadge>Month</PlaceholderBadge>}
        </Text>
      </div>
      <Divider color="neutral.2" />
      {lines.map((line, index) => {
        const showDivider = !!(line.separated ?? line.props?.separated);
        const lineStyle = lineStyles?.[line.name];
        const normalizedLineName = line.name.toLowerCase();

        const isPrimary =
          lineStyle?.isPrimary ??
          ['amount due', 'net income', 'earnings'].includes(normalizedLineName);

        const defaultIsBold =
          [
            'balance start',
            'balance forward',
            'balance end',
            'available balance',
          ].includes(normalizedLineName) || index === 0;

        const isBold = lineStyle?.isBold ?? defaultIsBold;

        return (
          <Fragment key={line.name}>
            {showDivider && <Divider color="neutral.2" />}
            <SummaryLine
              title={line.name}
              isPrimary={isPrimary}
              isBold={isBold}
            >
              {line.text}
            </SummaryLine>
          </Fragment>
        );
      })}
    </Card>
  );
};

const SummaryLine = ({
  isPrimary,
  children,
  title,
  isBold,
}: {
  isPrimary?: boolean;
  isBold?: boolean;
  title: string;
  children: React.ReactNode;
}) => {
  const textColor = isBold ? 'neutral.8' : 'neutral.6';
  const valueColor = isPrimary ? '#1343CC' : textColor;

  return (
    <Group justify="space-between" wrap="nowrap" pr={rem(8)}>
      <Text
        component="p"
        fw={isBold ? 500 : 400}
        c={textColor}
        m={0}
        fz={rem(14)}
        lh={1.4}
        style={{
          letterSpacing: '-0.09px',
        }}
      >
        {title}
      </Text>
      <Text
        component="p"
        fw={isBold ? 500 : 400}
        c={valueColor}
        m={0}
        fz={rem(14)}
        lh={1.4}
        ta="right"
        style={{
          letterSpacing: '-0.09px',
        }}
      >
        {children}
      </Text>
    </Group>
  );
};
